import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import get from 'lodash/get';
import { isEmpty, uniqueId } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { TextField as FluentTextField } from 'office-ui-fabric-react/lib/TextField';
import { Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { IconButton } from 'office-ui-fabric-react';
import { useLocation } from 'react-router-dom';
import Link from '../Link';
import StackGrid from '../StackGrid';
import { createRole, editRole } from '../../actions/roles';
import TextField from '../TextField';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import TextButton from '../TextButton';
import PermissionSearchList from '../../components/PermissionSearchList';
import styles from './CreateAndEditRoleForm.style';

const RoleSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  slug: Yup.string().required('Required'),
  permissions: Yup.array(
    Yup.object()
      .shape({
        isEnabled: Yup.boolean().required('Required'),
        permission: Yup.string().required('Required'),
      })
      .required('Required'),
  ).required('Required'),
});

const CreateAndEditRoleForm = React.forwardRef(({ roleDetails, onUpdate, onSuccess }, ref) => {
  const dispatch = useDispatch();
  const { roleFormData } = useLocation();
  const allPermissions = useSelector(state => get(state, 'entities.roles.admin.permissions', {}));

  const [initialValues, setinitialValues] = useState({
    name: '',
    slug: '',
    permissions: [],
  });

  const getKeyValuePermissions = permissions => {
    const permissionsForRole = permissions.map(permission => {
      let item = permission;
      if (!item.permission) {
        item = {
          permission: permission.permission,
          isEnabled: permission?.isEnabled || true,
          newId: permission.newId || null,
          isEditable: permission.isEditable || false,
        };
      }
      return item;
    });
    return permissionsForRole;
  };

  useEffect(() => {
    if (roleFormData) {
      if (Array.isArray(roleFormData.permissions)) {
        const associatedPermissions = getKeyValuePermissions(roleFormData.permissions);
        const updateRole = { ...roleFormData, permissions: associatedPermissions };
        const isPermissionsValid =
          roleFormData.permissions.length > 0 &&
          !associatedPermissions.some(item => item.errorMessage || !item.permission);
        onUpdate(isPermissionsValid);
        setinitialValues(updateRole);
      } else {
        setinitialValues(roleFormData);
      }
    }
  }, [roleFormData]);

  useEffect(() => {
    if (roleDetails && !roleFormData) {
      const permissionsForRole = Object.keys(roleDetails.permissions).map(permission => {
        const item = {
          permission,
          isEnabled: roleDetails.permissions[permission] ?? true,
        };

        return item;
      });
      const updateRole = { ...roleDetails, permissions: permissionsForRole };
      setinitialValues(updateRole);
    }
  }, [roleDetails]);

  return (
    <Formik
      innerRef={ref}
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      validationSchema={RoleSchema}
      onSubmit={async (values, { setErrors }) => {
        const submittedValues = { ...values };
        const newPermissions = submittedValues.permissions.filter(i => i.newId);
        const newlyAdded = newPermissions.reduce(
          (previousItem, item) => ({ ...previousItem, [item.permission.trim()]: true }),
          {},
        );
        submittedValues.permissions = submittedValues.permissions.reduce(
          (previousItem, item) => ({ ...previousItem, [item.permission]: item.isEnabled }),
          {},
        );

        if (!isEmpty(newlyAdded)) {
          const adminPermissions = { ...allPermissions, ...newlyAdded };
          submittedValues.adminPermissions = adminPermissions;
        }

        let result;
        if (roleDetails?.slug) {
          result = await dispatch(editRole(submittedValues));
        } else {
          result = await dispatch(createRole(submittedValues));
        }
        if (result.error && result.payload.status === 409) {
          setErrors({
            form: `Role with name ${submittedValues.name} or slug ${submittedValues.slug} already exists`,
          });
        } else if (result.error && result.payload.message) {
          const errorMessage = result.payload.message;
          setErrors({ form: errorMessage });
        }

        // if no errors and
        if (!result.error) {
          onSuccess(get(result, `payload.entities.roles.${result.payload.result}`));
        }
        return result;
      }}
    >
      {({ values, isSubmitting, errors, touched, setFieldValue }) => {
        const handleToggleChange = (e, checked, permissionItem) => {
          const { permissions } = values;
          const modifiedPermissions = permissions.map(item => {
            const modifyItem = item;
            if (modifyItem.permission === permissionItem.permission) {
              modifyItem.isEnabled = checked;
            }
            return modifyItem;
          });
          const isValidUpdate =
            modifiedPermissions.length > 0 &&
            !modifiedPermissions.some(item => item.errorMessage || !item.permission);
          onUpdate(isValidUpdate);
          setFieldValue('permissions', modifiedPermissions);
        };

        const handleRemoveItem = item => {
          const { permissions } = values;
          const updatedPermissions = item.newId
            ? permissions.filter(i => i.newId !== item.newId)
            : permissions.filter(i => i.permission !== item.permission);
          const isValidUpdate =
            updatedPermissions.length > 0 &&
            !updatedPermissions.some(
              eachPermission => eachPermission.errorMessage || !eachPermission.permission,
            );
          setFieldValue('permissions', updatedPermissions);
          onUpdate(isValidUpdate);
        };

        const handleBlur = (item, value) => {
          const { permissions } = values;
          const errorText = !/^([a-z]+)(([A-Z]([a-z]+))+)$/.test(value)
            ? 'Permission should be camel case'
            : '';
          const updatedPermissions = permissions.map(x =>
            x.newId === item.newId
              ? {
                  ...x,
                  errorMessage: Object.keys(allPermissions).find(
                    eachPermission => eachPermission.toLowerCase() === value.toLowerCase(),
                  )
                    ? 'Permission is already exists'
                    : errorText,
                }
              : x,
          );
          const isValidUpdate =
            updatedPermissions.length > 0 &&
            !updatedPermissions.some(
              permissionItem => permissionItem.errorMessage || !permissionItem.permission,
            );
          setFieldValue('permissions', updatedPermissions);
          onUpdate(isValidUpdate);
        };

        const handleChange = (item, value) => {
          const { permissions } = values;
          const updatedPermission = permissions.map(x =>
            x.newId === item.newId ? { ...x, permission: value } : x,
          );
          setFieldValue('permissions', updatedPermission);
        };

        const createNewPermission = () => {
          const { permissions } = values;
          const newItem = {
            newId: uniqueId(permissions.length),
            isEnabled: true,
            isEditable: true,
            permission: '',
          };
          const updatedPermission = [...permissions];
          updatedPermission.unshift(newItem);
          setFieldValue('permissions', updatedPermission);
          onUpdate(false);
        };

        const columns = [
          {
            key: 'icon',
            name: 'icon',
            headerClassName: styles.iconColumnHeader,
            ariaLabel: 'icon',
            isIconOnly: true,
            fieldName: 'icon',
            minWidth: 14,
            maxWidth: 14,
            columnActionsMode: 0,
            onRender: () => {
              return (
                <div className={styles.iconColumn}>
                  <FontIcon
                    iconName="PlayerSettings"
                    ariaLabel="PlayerSettings"
                    className={styles.detailRowIconColor}
                  />
                </div>
              );
            },
          },
          {
            key: 'permission',
            name: 'Permissions',
            fieldName: 'permission',
            minWidth: 500,
            maxWidth: 550,
            columnActionsMode: 0,
            isPadded: true,
            onRender: item => {
              const { isEditable, errorMessage } = item;
              if (isEditable) {
                return (
                  <div className={styles.permissionCell}>
                    <FluentTextField
                      name="permission"
                      ariaLabel="permission"
                      className={
                        errorMessage
                          ? clsx(styles.permissionBox, styles.errorBox)
                          : styles.permissionBox
                      }
                      value={item.permission}
                      onBlur={event => handleBlur(item, event.target.value)}
                      onChange={event => handleChange(item, event.target.value)}
                    />
                    {errorMessage && <span className={styles.errorMessage}>{errorMessage}</span>}
                  </div>
                );
              }
              return <span>{item.permission}</span>;
            },
          },
          {
            key: 'access',
            name: 'access',
            headerClassName: styles.iconColumnHeader,
            ariaLabel: 'accessIcon',
            isIconOnly: true,
            minWidth: 90,
            maxWidth: 90,
            columnActionsMode: 0,
            onRender: item => {
              return (
                <div className={styles.toggleColumn}>
                  <Toggle
                    checked={item.isEnabled}
                    onText="Enabled"
                    offText="Disabled"
                    className={styles.pill}
                    onChange={(e, checked) => handleToggleChange(e, checked, item)}
                  />
                </div>
              );
            },
          },
          {
            key: 'remove',
            name: 'Remove',
            arianame: 'remove',
            isIconOnly: true,
            fieldName: 'remove',
            headerClassName: styles.iconColumnHeader,
            minWidth: 25,
            maxWidth: 25,
            isPadded: true,
            onRender: (item, index) => {
              const iconName = 'Cancel';
              return (
                <IconButton
                  className={styles.removeButton}
                  ariaLabel={iconName}
                  iconProps={{ iconName }}
                  id={`iconButton${index}`}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleRemoveItem(item);
                  }}
                />
              );
            },
          },
        ];

        useEffect(() => {
          if (!isEmpty(touched)) {
            const isPermissionsValid =
              values?.permissions?.length > 0 &&
              !values?.permissions.some(
                eachPermission => eachPermission.errorMessage || !eachPermission.permission,
              );
            onUpdate(isPermissionsValid);
          }
        }, [touched]);

        return (
          <Form noValidate>
            {errors.form && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline
                dismissButtonAriaLabel="Close"
                nowrap
              >
                {errors.form}
              </MessageBar>
            )}
            <SubModule disableAccordian>
              <SubModuleHeader>General Information</SubModuleHeader>
              <SubModuleContent data-testid="bundleForm">
                <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                  <Field
                    name="name"
                    label="Role Name"
                    component={TextField}
                    disabled={isSubmitting}
                    data-testid="roleName-selector"
                    for="Role Name"
                    required
                  />
                  <Field
                    name="slug"
                    label="Slug"
                    disabled={roleDetails}
                    component={TextField}
                    data-testid="slug-selector"
                    for="Slug"
                    required
                  />
                </StackGrid>
              </SubModuleContent>
            </SubModule>

            <SubModule disableAccordian>
              <SubModuleHeader className={styles.headerContainer}>
                Permissions
                <TextButton
                  className={styles.createButton}
                  data-testid="select-all"
                  onClick={createNewPermission}
                >
                  {' '}
                  Create New Permissions
                </TextButton>
              </SubModuleHeader>
              <SubModuleContent>
                <Field name="permissions" data-testid="roleFormItems" required>
                  {({ field: { value } }) =>
                    !isEmpty(value) && (
                      <PermissionSearchList
                        columns={columns}
                        items={value}
                        className={styles.permissionList}
                        disableRowLink
                      />
                    )
                  }
                </Field>
                <Link
                  to={{
                    pathname: '/permissions/add-permissions',
                    roleFormData: values,
                    isEdit: !!roleDetails,
                  }}
                >
                  <TextButton>Add Permissions</TextButton>
                </Link>
              </SubModuleContent>
            </SubModule>
          </Form>
        );
      }}
    </Formik>
  );
});

CreateAndEditRoleForm.propTypes = {
  roleDetails: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    permissions: PropTypes.array,
  }),
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
};

CreateAndEditRoleForm.defaultProps = {
  roleDetails: null,
  onUpdate: () => {},
  onSuccess: () => {},
};

export default CreateAndEditRoleForm;
